import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box, Typography, Button, FormControl,
  Select, MenuItem, Stack, IconButton, CircularProgress,
  TextField, Chip, Tooltip,
  Collapse, Divider, Avatar,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SchoolIcon from '@mui/icons-material/School';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import { db, rtdb, auth, RTDB_URL } from '../firebase-config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { ref, get, child } from 'firebase/database';
import SpeedIcon from '@mui/icons-material/Speed';
import { DIFFICULTY_PATTERNS, dedupeQuestions, pickForExamType, questionTopic } from '../utils/examDifficulty';

const COLORS = [
  { bg: '#eff6ff', border: '#93c5fd', text: '#1d4ed8' },
  { bg: '#fdf4ff', border: '#d8b4fe', text: '#7e22ce' },
  { bg: '#fefce8', border: '#fcd34d', text: '#92400e' },
  { bg: '#ecfdf5', border: '#6ee7b7', text: '#065f46' },
  { bg: '#fff1f2', border: '#fca5a5', text: '#be123c' },
];

const CLASS_OPTIONS = [
  { val: '11', label: 'Class 11' },
  { val: '12', label: 'Class 12' },
  { val: 'both', label: 'Both' },
];

export const generateId = () => {
  const ts = Date.now().toString(36).toUpperCase();
  const rnd = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `EX-${ts}-${rnd}`;
};

/** Normalizes subject names to match RTDB keys */
const normalizeSubjectName = (name) => {
  if (!name) return '';
  const n = name.trim().toLowerCase();
  if (n.includes('math')) return 'Maths';
  if (n.includes('physics')) return 'Physics';
  if (n.includes('chemistry')) return 'Chemistry';
  if (n.includes('biology') || n.includes('botany') || n.includes('zoology')) return 'Biology';
  return name.trim();
};

export default function CreateExam({ onNextClick }) {

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [fetchingQuestions, setFetchingQuestions] = useState(false);
  const [copied, setCopied] = useState(false);
  const [examId, setExamId] = useState(generateId);
  const [template, setTemplate] = useState(null);
  const [classLevel, setClassLevel] = useState('');
  const [mode, setMode] = useState('chapter');
  const [topicSelections, setTopicSelections] = useState({}); // { subject: { value, label } }, empty = all topics
  const [topicsMap, setTopicsMap] = useState({});             // { subject: [{ value, label, top_qcount }] }
  const [loadingTopics, setLoadingTopics] = useState(null);   // subject name currently loading
  const [examPattern, setExamPattern] = useState('KCET');
  const [selectedSubjects, setSelectedSubjects] = useState(new Set());
  const [chapterSelections, setChapterSelections] = useState({});
  const [chaptersMap, setChaptersMap] = useState({});
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [expandedSubject, setExpandedSubject] = useState(null);
  const [availableSubjects, setAvailableSubjects] = useState(new Set());
  const [subjectKeyMap, setSubjectKeyMap] = useState({});
  const [loadingAvailable, setLoadingAvailable] = useState(false);

  useEffect(() => { loadTemplates(); }, []);

  useEffect(() => {
    if (classLevel && template) {
      loadAvailableSubjects();
    } else {
      setAvailableSubjects(new Set());
      setSubjectKeyMap({});
    }
  }, [classLevel, template]);

  const fetchShallow = async (path) => {
    try {
      const baseUrl = RTDB_URL;
      const encodedPath = path.split('/').map(encodeURIComponent).join('/');
      let url = `${baseUrl}/${encodedPath}.json?shallow=true`;
      
      try {
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        if (token) url += `&auth=${token}`;
      } catch (e) { /* no token: shallow fetch still works if rules allow */ }

      const res = await fetch(url);
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  };

  const loadAvailableSubjects = async () => {
    if (!classLevel || !template) return;
    setLoadingAvailable(true);
    try {
      const dbRef = ref(rtdb);
      const templateSubs = template?.subjects || [];
      const targetClasses = classLevel === 'both' ? ['11', '12'] : [classLevel];
      
      const getWithTimeout = async (path) => {
        const fetchPromise = get(child(dbRef, path));
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error(`Timeout fetching ${path}`)), 8000)
        );
        return Promise.race([fetchPromise, timeoutPromise]);
      };

      // All paths in parallel: sequential lookups hit the timeouts
      await Promise.all(templateSubs.flatMap(sub => {
        const tName = sub.subject || sub.name;
        const tNorm = normalizeSubjectName(tName);
        
        return targetClasses.flatMap(cls => {
          const classPaths = [`prod/${cls}`, `prod/Class ${cls}`];
          return classPaths.flatMap(cp => {
            const sKeys = [tName, tNorm];
            return sKeys.map(async (sKey) => {
              try {
                const path = `${cp}/${sKey}/chapters`;
                let data = await fetchShallow(path);
                
                if (!data) {
                  const snapshot = await getWithTimeout(path);
                  if (snapshot.exists()) data = snapshot.val();
                }

                if (data && typeof data === 'object') {
                  setAvailableSubjects(prev => new Set([...prev, tNorm]));
                  setSubjectKeyMap(prev => ({
                    ...prev,
                    [tNorm]: Array.from(new Set([...(prev[tNorm] || []), sKey]))
                  }));

                  const chNames = Object.keys(data)
                    .filter(ch => ch && !['chapter_details', 'undefined', 'null'].includes(ch))
                    .map(ch => ch.trim())
                    .sort();
                  
                  setChaptersMap(prev => ({
                    ...prev,
                    [tName]: Array.from(new Set([...(prev[tName] || []), ...chNames])).sort()
                  }));
                }
              } catch (err) {
                console.warn(`Verify failed for ${sKey} at ${cp}:`, err.message);
              }
            });
          });
        });
      }));
    } catch (e) {
      console.error("Critical error in loadAvailableSubjects:", e);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    let list = [];
    try {
      try {
        let snap = await getDocs(query(collection(db, 'exam_templates'), where('isStudentVisible', '==', true)));
        if (snap.empty) snap = await getDocs(query(collection(db, 'examTemplates'), where('isStudentVisible', '==', true)));
        if (snap.empty) snap = await getDocs(collection(db, 'examTemplates'));
        
        if (!snap.empty) {
          list = snap.docs.map(d => ({ docId: d.id, ...d.data() }));
        }
      } catch (e) { console.error("Firestore Template Error:", e); }

      if (list.length === 0) {
        const dbRef = ref(rtdb);
        const paths = ['exam_templates', 'examTemplates', 'templates', 'Templates', 'ExamTemplates'];
        
        for (const path of paths) {
          const snapshot = await get(child(dbRef, path));
          if (snapshot.exists()) {
            const data = snapshot.val();
            if (Array.isArray(data)) {
              list = data.filter(Boolean).map((t, i) => ({ docId: String(i), ...t }));
            } else if (typeof data === 'object' && data !== null) {
              list = Object.entries(data).map(([id, val]) => ({ docId: id, ...val }));
            }
            if (list.length > 0) break;
          }
        }
      }
      
      setTemplates(list.filter(t => t.isStudentVisible !== false && t.isStudentVisible !== 'false' && t.isStudentVisible !== 0));
    } catch (e) { 
      console.error("Template Loading Error:", e); 
    }
    finally { setLoadingTemplates(false); }
  };

  const handleTemplateChange = (e) => {
    applyTemplate(templates.find(t => (t.id || t.docId) === e.target.value) || null);
  };

  const applyTemplate = (sel) => {
    setTemplate(sel);
    const templateType = String(sel?.examType || '').toUpperCase();
    if (DIFFICULTY_PATTERNS[templateType]) setExamPattern(templateType);
    setSelectedSubjects(new Set()); 
    setChapterSelections({});
    setTopicSelections({}); setTopicsMap({});
    setChaptersMap({}); 
    setExpandedSubject(null);
  };

  const toggleSubject = (name) =>
    setSelectedSubjects(prev => {
      const n = new Set(prev);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });

  const loadChapters = async (subName) => {
    if (chaptersMap[subName] !== undefined && chaptersMap[subName].length > 0) return;
    setLoadingChapters(true);
    try {
      const dbRef = ref(rtdb);
      const set = new Set();
      const normSub = normalizeSubjectName(subName);
      const targetClasses = classLevel === 'both' ? ['11', '12'] : [classLevel];
      const possibleOriginalKeys = subjectKeyMap[normSub] || [subName];
      
      const getWithTimeout = async (path) => {
        const fetchPromise = get(child(dbRef, path));
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 6000)
        );
        return Promise.race([fetchPromise, timeoutPromise]);
      };

      await Promise.all(targetClasses.flatMap(cls => {
        const classPaths = [`prod/${cls}`, `prod/Class ${cls}`, `${cls}`, `Class ${cls}`];
        return classPaths.flatMap(cp => {
          const subKeys = [subName, normSub, ...possibleOriginalKeys];
          return subKeys.map(async (sKey) => {
            try {
              const path = `${cp}/${sKey}/chapters`;
              let chaptersData = await fetchShallow(path);
              
              if (!chaptersData) {
                const snapshot = await getWithTimeout(path);
                if (snapshot.exists()) chaptersData = snapshot.val();
              }

              if (typeof chaptersData === 'object' && chaptersData !== null) {
                Object.keys(chaptersData).forEach(chName => {
                  if (chName && !['chapter_details', 'undefined', 'null'].includes(chName)) {
                    set.add(chName.trim());
                  }
                });
              }
            } catch (err) { console.warn("Fallback chapter fetch error:", err); }
          });
        });
      }));
      
      setChaptersMap(p => ({ ...p, [subName]: Array.from(set).sort() }));
    } catch (e) { 
      console.error("Error loading chapters:", e);
    }
    finally { setLoadingChapters(false); }
  };

  const loadTopics = async (subName, chapter) => {
    setLoadingTopics(subName);
    try {
      const normSub = normalizeSubjectName(subName);
      const targetClasses = classLevel === 'both' ? ['11', '12'] : [classLevel];
      const subKeys = Array.from(new Set([subName, normSub, ...(subjectKeyMap[normSub] || [])]));
      const found = new Map();

      await Promise.all(targetClasses.flatMap(cls =>
        [`prod/${cls}`, `prod/Class ${cls}`].flatMap(cp =>
          subKeys.map(async (sKey) => {
            try {
              const snap = await get(child(ref(rtdb), `${cp}/${sKey}/chapters/${chapter}/topic_details`));
              if (!snap.exists()) return;
              const data = snap.val();
              (Array.isArray(data) ? data : Object.values(data || {})).forEach(t => {
                if (t && t.label && t.value !== undefined) found.set(String(t.value), t);
              });
            } catch (err) { console.warn('Topic fetch error:', err); }
          })
        )
      ));

      setTopicsMap(p => ({ ...p, [subName]: [...found.values()].sort((a, b) => String(a.label).localeCompare(String(b.label))) }));
    } finally {
      setLoadingTopics(null);
    }
  };

  const handleChapterChange = (subName, chapter) => {
    setChapterSelections(p => ({ ...p, [subName]: chapter }));
    setTopicSelections(p => ({ ...p, [subName]: null }));
    loadTopics(subName, chapter);
  };

  const toggleExpand = async (name) => {
    const open = expandedSubject === name;
    setExpandedSubject(open ? null : name);
    if (!open) await loadChapters(name);
  };

  const templateSubjects = template?.subjects || [];
  const chapterCount = Object.values(chapterSelections).filter(Boolean).length;
  const isReady =
    template && classLevel &&
    (mode === 'subject' ? selectedSubjects.size > 0 : chapterCount > 0);

  const handleGenerate = async () => {
    if (!isReady) return;
    setFetchingQuestions(true);
    try {
      const subjectsMap = {};
      const targetClasses = classLevel === 'both' ? ['11', '12'] : [classLevel];
      const baseUrl = RTDB_URL;
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;

      const fastFetch = async (path, limit = 100) => {
        try {
          const encodedPath = path.split('/').map(encodeURIComponent).join('/');
          // IMPORTANT: Firebase REST API requires orderBy when using limitToFirst
          let url = `${baseUrl}/${encodedPath}.json?limitToFirst=${limit}&orderBy="$key"`;
          if (token) url += `&auth=${token}`;
          const res = await fetch(url);
          if (!res.ok) return null;
          return await res.json();
        } catch (e) { return null; }
      };

      console.log(`Starting generation for ${templateSubjects.length} subjects...`);
      
      await Promise.all(templateSubjects.map(async (sub) => {
        const nameFromTemplate = sub.subject || sub.name || '';
        const dbName = normalizeSubjectName(nameFromTemplate);
        const n = sub.numQuestions || 30;
        let pool = [];

        if (mode === 'subject') {
          if (!selectedSubjects.has(nameFromTemplate)) return;
          
          await Promise.all(targetClasses.flatMap(cls => {
            const classPaths = [`prod/${cls}`, `prod/Class ${cls}`];
            const possibleKeys = Array.from(new Set([nameFromTemplate, dbName, ...(subjectKeyMap[dbName] || [])]));

            return classPaths.flatMap(cp => 
              possibleKeys.map(async (sKey) => {
                if (pool.length >= n * 2) return;
                
                const path = `${cp}/${sKey}/chapters`;
                console.log(`Checking subject: ${path}`);
                const chaptersMeta = await fetchShallow(path);
                
                if (chaptersMeta) {
                  const chKeys = Object.keys(chaptersMeta).slice(0, 15);
                  await Promise.all(chKeys.map(async (chKey) => {
                    if (pool.length >= n * 4) return;
                    
                    const subPaths = [
                      `${path}/${chKey}/chapter_details/questions`,
                      `${path}/${chKey}/questions`
                    ];
                    
                    for (const sp of subPaths) {
                      let qs = await fastFetch(sp, 100);
                      if (!qs) {
                        try {
                          const snap = await get(child(ref(rtdb), sp));
                          if (snap.exists()) qs = snap.val();
                        } catch (e) {}
                      }
                      
                      if (qs) {
                        const list = Array.isArray(qs) ? qs.filter(Boolean) : Object.values(qs);
                        pool = [...pool, ...list.map(q => ({ ...q, chapterName: chKey }))];
                        console.log(`Found ${list.length} questions in ${sp}`);
                        break;
                      }
                    }
                  }));
                } else {
                  const directPaths = [`${cp}/${sKey}/chapter_details/questions`, `${cp}/${sKey}/questions`];
                  for (const dp of directPaths) {
                    let qs = await fastFetch(dp, 200);
                    if (!qs) {
                      try {
                        const snap = await get(child(ref(rtdb), dp));
                        if (snap.exists()) qs = snap.val();
                      } catch (e) {}
                    }
                    if (qs) {
                      const list = Array.isArray(qs) ? qs.filter(Boolean) : Object.values(qs);
                      pool = [...pool, ...list];
                      console.log(`Found ${list.length} questions in direct path: ${dp}`);
                    }
                  }
                }
              })
            );
          }));
        } else {
          const selCh = chapterSelections[nameFromTemplate];
          if (!selCh) return;

          await Promise.all(targetClasses.flatMap(cls => {
            const classPaths = [`prod/${cls}`, `prod/Class ${cls}`];
            const possibleKeys = Array.from(new Set([nameFromTemplate, dbName, ...(subjectKeyMap[dbName] || [])]));

            return classPaths.flatMap(cp => 
              possibleKeys.map(async (sKey) => {
                const paths = [
                  `${cp}/${sKey}/chapters/${selCh}/chapter_details/questions`,
                  `${cp}/${sKey}/${selCh}/chapter_details/questions`,
                  `${cp}/${sKey}/chapters/${selCh}/questions`,
                  `${cp}/${sKey}/${selCh}/questions`
                ];
                
                for (const path of paths) {
                  console.log(`Checking chapter path: ${path}`);
                  // whole chapter, so topic + difficulty filtering has the full set to pick from
                  let qs = await fastFetch(path, 2000);
                  if (!qs) {
                    try {
                      const snap = await get(child(ref(rtdb), path));
                      if (snap.exists()) qs = snap.val();
                    } catch (e) {}
                  }
                  
                  if (qs) {
                    const list = Array.isArray(qs) ? qs.filter(Boolean) : Object.values(qs);
                    pool = [...pool, ...list.map(q => ({ ...q, chapterName: selCh }))];
                    console.log(`Found ${list.length} questions in chapter path: ${path}`);
                    break; 
                  }
                }
              })
            );
          }));
        }

        if (mode === 'chapter') {
          pool = dedupeQuestions(pool);
          const topic = topicSelections[nameFromTemplate];
          if (topic) pool = pool.filter(q => questionTopic(q) === Number(topic.value));
          pool = pickForExamType(pool, n, examPattern);
          console.log(`${nameFromTemplate}: ${pool.length} questions after topic/difficulty filter`);
        }

        if (pool.length > 0) {
          subjectsMap[nameFromTemplate] = pool.sort(() => Math.random() - 0.5).slice(0, n);
        }
      }));

      const total = Object.values(subjectsMap).reduce((a, s) => a + s.length, 0);
      if (total === 0) {
        alert('No questions found for your selection. Please check the browser console (F12) for details on attempted paths.');
        return;
      }

      onNextClick({
        id: examId,
        name: `${template.name} — ${mode === 'subject' ? 'Subject' : 'Chapter'} Exam`,
        examName: `${template.name} — ${mode === 'subject' ? 'Subject' : 'Chapter'} Exam`,
        duration: templateSubjects.reduce((a, s) => a + (s.duration || 0), 0) || 120,
        totalQuestions: total,
        templateId: template.id || template.docId,
        examType: String(template.examType || '').toUpperCase(),
        class: classLevel,
        type: 'create_own_exam',
        granularity: mode,
        chapterSelections: mode === 'chapter' ? chapterSelections : null,
        topicSelections: mode === 'chapter'
          ? Object.fromEntries(Object.entries(topicSelections).filter(([, t]) => t).map(([sub, t]) => [sub, t.label]))
          : null,
        difficultyPattern: mode === 'chapter' ? examPattern : null,
        subjects: subjectsMap,
      });
    } catch (e) {
      console.error("Exam Generation Error:", e);
      alert('Error generating exam. Please try again.');
    } finally {
      setFetchingQuestions(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1080 }}>
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Box>
          <Typography component="h1" sx={{ fontWeight: 700, color: '#0f172a', fontSize: { xs: '26px', md: '34px' }, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
            Create Your Own Exam
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: { xs: '14px', md: '16px' }, mt: 1, maxWidth: 560, lineHeight: 1.6 }}>
            Select your preferences and generate a custom exam tailored to your goals. Practice smarter. Perform better.
          </Typography>
        </Box>
      </Box>

      <Paper elevation={0} sx={{
        borderRadius: '16px', border: '1px solid #e2e8f0', bgcolor: '#ffffff',
        boxShadow: '0 1px 3px rgba(15,23,42,0.04)', p: { xs: 2.5, md: 4 },
      }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 4, rowGap: 3.5 }}>

          <Box>
            <FieldLabel Icon={SchoolIcon}>Class</FieldLabel>
            <FormControl fullWidth>
              <Select
                displayEmpty
                value={classLevel}
                onChange={e => setClassLevel(e.target.value)}
                renderValue={val => val
                  ? CLASS_OPTIONS.find(o => o.val === val)?.label
                  : <Typography component="span" sx={{ color: '#94a3b8' }}>Select class…</Typography>
                }
                sx={selectSx}
              >
                {CLASS_OPTIONS.map(opt => (
                  <MenuItem key={opt.val} value={opt.val}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <FieldLabel Icon={DescriptionOutlinedIcon}>Exam Template</FieldLabel>
            <FormControl fullWidth>
              <Select
                displayEmpty
                value={template ? (template.id || template.docId) : ''}
                onChange={handleTemplateChange}
                renderValue={val => val
                  ? template?.name
                  : <Typography component="span" sx={{ color: '#94a3b8' }}>Select a template…</Typography>
                }
                sx={selectSx}
              >
                {loadingTemplates
                  ? <MenuItem disabled><CircularProgress size={15} sx={{ mr: 1 }} /> Loading…</MenuItem>
                  : templates.map(t => (
                    <MenuItem key={t.id || t.docId} value={t.id || t.docId} sx={{ py: 1.5 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1e293b' }}>{t.name}</Typography>
                        <Typography sx={{ fontSize: '11px', color: '#64748b', mt: 0.2 }}>
                          {t.subjects?.length || 0} subjects
                          {' · '}
                          {t.subjects?.reduce((a, s) => a + (s.numQuestions || 0), 0) || 0} questions
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>

            {template && templateSubjects.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 1.5 }}>
                {templateSubjects.map((s, i) => {
                  const c = COLORS[i % COLORS.length];
                  return (
                    <Chip
                      key={i} size="small"
                      label={`${s.subject || s.name}  ·  ${s.numQuestions || 0}Q`}
                      sx={{ bgcolor: c.bg, color: c.text, border: `1.5px solid ${c.border}`, fontWeight: 700, fontSize: '11px', borderRadius: '8px' }}
                    />
                  );
                })}
              </Box>
            )}
          </Box>

          <Box sx={{ gridColumn: mode === 'chapter' ? 'auto' : '1 / -1' }}>
            <FieldLabel Icon={TuneIcon}>Question Mode</FieldLabel>
            <Box sx={{
              display: 'inline-flex', bgcolor: '#f1f5f9', borderRadius: '12px', p: '4px', gap: '4px',
            }}>
              {[
                { val: 'chapter', label: 'Chapter-wise', Icon: MenuBookIcon },
                { val: 'subject', label: 'Subject-wise', Icon: SchoolIcon },
              ].map(opt => {
                const active = mode === opt.val;
                return (
                  <Box
                    key={opt.val}
                    role="button"
                    aria-pressed={active}
                    onClick={() => {
                      setMode(opt.val);
                      setSelectedSubjects(new Set());
                      setChapterSelections({});
                      setTopicSelections({});
                      setExpandedSubject(null);
                    }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.85,
                      px: { xs: 1.75, sm: 2.5 }, py: 1.2, borderRadius: '10px', cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      bgcolor: active ? '#fff' : 'transparent',
                      boxShadow: active ? '0 2px 8px rgba(0,0,0,0.09)' : 'none',
                      color: active ? '#1d4ed8' : '#64748b',
                      transition: 'all 0.18s',
                      userSelect: 'none',
                    }}
                  >
                    <opt.Icon sx={{ fontSize: 16 }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: active ? 800 : 600, color: 'inherit' }}>
                      {opt.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Exam pattern picks the 50/50 difficulty mix (see utils/examDifficulty) */}
          {mode === 'chapter' && (
            <Box>
              <FieldLabel Icon={SpeedIcon}>Difficulty</FieldLabel>
              <Box role="radiogroup" aria-label="Difficulty pattern" sx={{ display: 'inline-flex', bgcolor: '#f1f5f9', borderRadius: '12px', p: '4px', gap: '4px' }}>
                {Object.keys(DIFFICULTY_PATTERNS).map(key => {
                  const active = examPattern === key;
                  return (
                    <Box
                      key={key}
                      role="radio"
                      aria-checked={active}
                      tabIndex={0}
                      onClick={() => setExamPattern(key)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExamPattern(key); } }}
                      sx={{
                        px: { xs: 2, sm: 2.5 }, py: 1.2, borderRadius: '10px', cursor: 'pointer', userSelect: 'none',
                        bgcolor: active ? '#fff' : 'transparent',
                        boxShadow: active ? '0 2px 8px rgba(0,0,0,0.09)' : 'none',
                        color: active ? '#1d4ed8' : '#64748b',
                        fontSize: '13px', fontWeight: active ? 800 : 600,
                        transition: 'all 0.18s',
                        '&:focus-visible': { outline: '2px solid #1d4ed8', outlineOffset: 2 },
                      }}
                    >
                      {key}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          <Box sx={{ gridColumn: '1 / -1' }}>
            {mode === 'subject'
              ? <SubjectPicker
                emptyHint="Select a template to see subjects."
                subjects={templateSubjects}
                selected={selectedSubjects}
                onToggle={toggleSubject}
                availableSubjects={availableSubjects}
                loadingAvailable={loadingAvailable}
              />
              : <ChapterPicker
                emptyHint="Select a template to see subjects."
                subjects={templateSubjects}
                selections={chapterSelections}
                chaptersMap={chaptersMap}
                expandedSubject={expandedSubject}
                loadingChapters={loadingChapters}
                availableSubjects={availableSubjects}
                loadingAvailable={loadingAvailable}
                onExpand={toggleExpand}
                onChapterChange={handleChapterChange}
                topicSelections={topicSelections}
                topicsMap={topicsMap}
                loadingTopics={loadingTopics}
                onTopicChange={(sub, topic) => setTopicSelections(p => ({ ...p, [sub]: topic }))}
              />
            }
          </Box>
        </Box>

        <Divider sx={{ my: { xs: 3, md: 4 }, borderColor: '#f1f5f9' }} />

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'flex-end' } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FieldLabel Icon={KeyIcon}>Exam ID</FieldLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth size="small"
                value={examId}
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontFamily: '"JetBrains Mono","Fira Code",monospace',
                    fontSize: '12.5px', fontWeight: 700,
                    color: '#1d4ed8', letterSpacing: '0.06em',
                    bgcolor: '#eff6ff', borderRadius: '10px',
                    border: '1.5px solid #bfdbfe',
                    '& fieldset': { border: 'none' },
                    '& .MuiInputBase-input': { py: '10px' },
                  }
                }}
              />
              <Tooltip title={copied ? 'Copied' : 'Copy ID'} arrow>
                <IconButton
                  size="small"
                  aria-label="Copy exam ID"
                  onClick={() => { navigator.clipboard.writeText(examId); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  sx={{ bgcolor: copied ? '#ecfdf5' : '#f1f5f9', border: `1.5px solid ${copied ? '#6ee7b7' : '#e2e8f0'}`, borderRadius: '10px', p: 1.1, transition: 'all 0.2s' }}
                >
                  <ContentCopyIcon sx={{ fontSize: 16, color: copied ? '#10b981' : '#64748b' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Regenerate ID" arrow>
                <IconButton
                  size="small"
                  aria-label="Regenerate exam ID"
                  onClick={() => setExamId(generateId())}
                  sx={{ bgcolor: '#f1f5f9', border: '1.5px solid #e2e8f0', borderRadius: '10px', p: 1.1 }}
                >
                  <AutorenewIcon sx={{ fontSize: 16, color: '#64748b' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Button
              fullWidth
              variant="contained"
              disabled={!isReady || fetchingQuestions}
              onClick={handleGenerate}
              startIcon={fetchingQuestions ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{
                py: 1.5,
                borderRadius: '12px',
                bgcolor: '#1d4ed8',
                boxShadow: 'none',
                fontWeight: 700, fontSize: '15px', textTransform: 'none',
                '&:hover': { bgcolor: '#1e40af', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
              }}
            >
              {fetchingQuestions ? 'Preparing Exam…' : 'Generate Exam'}
            </Button>
          </Box>
        </Box>

        {!isReady && (
          <Typography sx={{ textAlign: { xs: 'center', md: 'right' }, mt: 1.25, fontSize: '12px', color: '#94a3b8', fontWeight: 600 }}>
            {!classLevel ? '① Choose a class'
              : !template ? '② Select a template'
                : mode === 'subject' && selectedSubjects.size === 0 ? '③ Pick at least one subject'
                  : '③ Pick at least one chapter per subject'}
          </Typography>
        )}
      </Paper>
    </Box>
  );
}

export const FieldLabel = ({ Icon, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
    <Icon sx={{ fontSize: 24, color: '#0f172a' }} />
    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#0f172a' }}>{children}</Typography>
  </Box>
);

export const selectSx = {
  borderRadius: '12px', bgcolor: '#ffffff',
  fontWeight: 500, fontSize: '15px', color: '#0f172a',
  '& .MuiSelect-select': { py: '14px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#94a3b8' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1d4ed8' },
};

function SubjectPicker({ emptyHint, subjects, selected, onToggle, availableSubjects, loadingAvailable }) {
  const filteredSubjects = subjects.filter(sub => {
    const name = sub.subject || sub.name || '';
    const dbName = normalizeSubjectName(name);
    return availableSubjects.size === 0 || availableSubjects.has(dbName);
  });

  return (
    <Box>
      <FieldLabel Icon={MenuBookIcon}>Subjects</FieldLabel>
      {filteredSubjects.length === 0
        ? <Typography sx={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic' }}>
          {subjects.length === 0 ? emptyHint : 'No matching subjects found in database for this class.'}
        </Typography>
        : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2 }}>
            {filteredSubjects.map((sub, i) => {
              const name = sub.subject || sub.name || `Subject ${i + 1}`;
              const isOn = selected.has(name);
              const c = COLORS[i % COLORS.length];
              return (
                <Chip
                  key={name}
                  label={`${name} (${sub.numQuestions || 30} Qs)`}
                  onClick={() => onToggle(name)}
                  icon={isOn ? <CheckCircleIcon style={{ fontSize: 15, color: c.text }} /> : undefined}
                  sx={{
                    bgcolor: isOn ? c.bg : '#f8fafc',
                    border: `2px solid ${isOn ? c.border : '#e2e8f0'}`,
                    color: isOn ? c.text : '#64748b',
                    fontWeight: isOn ? 800 : 600,
                    fontSize: '13px',
                    borderRadius: '10px',
                    height: '36px',
                    cursor: 'pointer',
                    transition: 'all 0.16s',
                    '& .MuiChip-icon': { ml: '8px' },
                    '&:hover': { bgcolor: c.bg, borderColor: c.border, color: c.text },
                  }}
                />
              );
            })}
          </Box>
        )
      }
      {selected.size > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5 }}>
          <CheckCircleIcon sx={{ color: '#10b981', fontSize: 16 }} />
          <Typography sx={{ fontSize: '12px', color: '#10b981', fontWeight: 700 }}>
            {selected.size} subject{selected.size > 1 ? 's' : ''} selected
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function ChapterPicker({ emptyHint, subjects, selections, chaptersMap, expandedSubject, loadingChapters, onExpand, onChapterChange, availableSubjects, loadingAvailable, topicSelections, topicsMap, loadingTopics, onTopicChange }) {
  const filteredSubjects = subjects.filter(sub => {
    const name = sub.subject || sub.name || '';
    const dbName = normalizeSubjectName(name);
    return availableSubjects.size === 0 || availableSubjects.has(dbName);
  });

  const doneCount = Object.values(selections).filter(Boolean).length;
  return (
    <Box>
      <FieldLabel Icon={MenuBookIcon}>Chapter per Subject</FieldLabel>
      {filteredSubjects.length === 0
        ? <Typography sx={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic' }}>
          {subjects.length === 0 ? emptyHint : 'No matching subjects found in database for this class.'}
        </Typography>
        : (
          <Stack spacing={1.2}>
            {filteredSubjects.map((sub, i) => {
              const name = sub.subject || sub.name || `Subject ${i + 1}`;
              const c = COLORS[i % COLORS.length];
              const chapters = chaptersMap[name] || [];
              const selected = selections[name] || '';
              const isOpen = expandedSubject === name;

              return (
                <Box key={name} sx={{
                  border: `2px solid ${isOpen || selected ? c.border : '#e2e8f0'}`,
                  borderRadius: '14px', overflow: 'clip',
                  bgcolor: isOpen ? c.bg : '#fff',
                  transition: 'all 0.18s',
                  boxShadow: isOpen ? `0 4px 16px rgba(0,0,0,0.06)` : 'none',
                }}>
                  <Box
                    onClick={() => onExpand(name)}
                    sx={{
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', px: 2, py: 1.5,
                      cursor: 'pointer', '&:hover': { bgcolor: c.bg },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{
                        width: 32, height: 32, fontSize: '13px', fontWeight: 800,
                        bgcolor: c.border, color: c.text,
                      }}>
                        {name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1e293b', lineHeight: 1.2 }}>
                          {name}
                        </Typography>
                        {selected
                          ? <Typography sx={{ fontSize: '11px', color: c.text, fontWeight: 600 }}>{selected}</Typography>
                          : <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>Tap to choose chapter</Typography>
                        }
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {selected && <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />}
                      {isOpen
                        ? <ExpandLessIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                        : <ExpandMoreIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                      }
                    </Box>
                  </Box>

                  <Collapse in={isOpen}>
                    <Divider sx={{ borderColor: c.border, opacity: 0.6 }} />
                    <Box sx={{
                      p: 2, bgcolor: '#fafafa',
                      display: { md: 'grid' }, gridTemplateColumns: { md: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 3, alignItems: 'start',
                    }}>
                      <Box>
                      <Typography sx={{ display: { xs: 'none', md: 'block' }, fontSize: '13px', fontWeight: 700, color: '#334155', mb: 1.25 }}>
                        Chapters
                      </Typography>
                      {chapters.length === 0
                        ? <Typography sx={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>
                            {loadingChapters ? 'Loading chapters...' : 'No chapter data found — all questions will be used.'}
                          </Typography>
                        : (
                            <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
                              {chapters.map(ch => {
                                const isSelected = selected === ch;
                                return (
                                  <Chip
                                    key={ch}
                                    label={ch}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onChapterChange(name, ch);
                                    }}
                                    sx={{
                                      bgcolor: isSelected ? c.border : '#fff',
                                      color: isSelected ? c.text : '#64748b',
                                      border: `1.5px solid ${isSelected ? c.text : '#e2e8f0'}`,
                                      fontWeight: isSelected ? 800 : 500,
                                      fontSize: '12px',
                                      borderRadius: '8px',
                                      transition: '0.2s',
                                      width: '100%',
                                      justifyContent: 'flex-start',
                                      '& .MuiChip-label': { width: '100%', textAlign: 'left' },
                                      '&:hover': {
                                        bgcolor: isSelected ? c.border : c.bg,
                                        borderColor: c.text
                                      }
                                    }}
                                  />
                                );
                              })}
                            </Stack>
                          )
                      }
                      </Box>

                      {/* desktop: topics sit beside the chapter list, pinned while it scrolls */}
                      <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'sticky', top: 16 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#334155', mb: 1.25 }}>
                          Topics{selected ? <Typography component="span" sx={{ fontSize: '13px', fontWeight: 500, color: '#64748b' }}> in {selected}</Typography> : null}
                        </Typography>
                        {!selected
                          ? <Typography sx={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>Pick a chapter to see its topics.</Typography>
                          : loadingTopics === name
                            ? <Typography sx={{ fontSize: '13px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={14} /> Loading topics…</Typography>
                            : (
                              <Stack spacing={1}>
                                {[{ value: '', label: 'All topics' }, ...(topicsMap?.[name] || [])].map(t => {
                                  const isSel = String(topicSelections?.[name]?.value ?? '') === String(t.value);
                                  return (
                                    <Chip
                                      key={`t-${t.value}`}
                                      label={
                                        <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                          <span>{t.label}</span>
                                          {t.top_qcount ? <Box component="span" sx={{ color: '#94a3b8', fontWeight: 500 }}>{t.top_qcount} Qs</Box> : null}
                                        </Box>
                                      }
                                      aria-pressed={isSel}
                                      onClick={(e) => { e.stopPropagation(); onTopicChange(name, t.value === '' ? null : { value: t.value, label: t.label }); }}
                                      sx={{
                                        bgcolor: isSel ? c.border : '#fff',
                                        color: isSel ? c.text : '#64748b',
                                        border: `1.5px solid ${isSel ? c.text : '#e2e8f0'}`,
                                        fontWeight: isSel ? 800 : 500,
                                        fontSize: '12px',
                                        borderRadius: '8px',
                                        width: '100%',
                                        justifyContent: 'flex-start',
                                        '& .MuiChip-label': { width: '100%', textAlign: 'left' },
                                        '&:hover': { bgcolor: isSel ? c.border : c.bg, borderColor: c.text },
                                      }}
                                    />
                                  );
                                })}
                                {(topicsMap?.[name] || []).length === 0 && (
                                  <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>No topics listed for this chapter; the whole chapter will be used.</Typography>
                                )}
                              </Stack>
                            )
                        }
                      </Box>
                    </Box>
                  </Collapse>

                  {selected && (
                    <Box sx={{ px: 2, pb: 1.75, pt: isOpen ? 1.75 : 0.25, display: { xs: 'flex', md: isOpen ? 'none' : 'flex' }, alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Typography component="label" htmlFor={`topic-${i}`} sx={{ fontSize: '13px', fontWeight: 600, color: '#334155', minWidth: 44 }}>
                        Topic
                      </Typography>
                      <FormControl size="small" sx={{ flex: 1, minWidth: 200 }}>
                        <Select
                          id={`topic-${i}`}
                          displayEmpty
                          disabled={loadingTopics === name}
                          value={topicSelections?.[name]?.value ?? ''}
                          onChange={(e) => {
                            const t = (topicsMap?.[name] || []).find(x => String(x.value) === String(e.target.value));
                            onTopicChange(name, t ? { value: t.value, label: t.label } : null);
                          }}
                          renderValue={(val) => val === ''
                            ? (loadingTopics === name ? 'Loading topics…' : 'All topics')
                            : topicSelections?.[name]?.label}
                          sx={{ ...selectSx, fontSize: '14px', '& .MuiSelect-select': { py: '9px' } }}
                        >
                          <MenuItem value="">All topics</MenuItem>
                          {(topicsMap?.[name] || []).map(t => (
                            <MenuItem key={t.value} value={t.value}>
                              {t.label}
                              {t.top_qcount ? <Typography component="span" sx={{ ml: 1, fontSize: '12px', color: '#94a3b8' }}>{t.top_qcount} Qs</Typography> : null}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        )
      }
      {doneCount > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5 }}>
          <CheckCircleIcon sx={{ color: '#10b981', fontSize: 16 }} />
          <Typography sx={{ fontSize: '12px', color: '#10b981', fontWeight: 700 }}>
            {doneCount} chapter{doneCount > 1 ? 's' : ''} selected
          </Typography>
        </Box>
      )}
    </Box>
  );
}
